import { toConsumableArray as _toConsumableArray, createClass as _createClass, regeneratorRuntime as _regeneratorRuntime, defineProperty as _defineProperty, inherits as _inherits, createForOfIteratorHelper as _createForOfIteratorHelper, objectSpread2 as _objectSpread2, classCallCheck as _classCallCheck, callSuper as _callSuper, assertThisInitialized as _assertThisInitialized, objectWithoutProperties as _objectWithoutProperties } from '../../../_virtual/_rollupPluginBabelHelpers.esm.js';
import React, { useRef, useCallback, useMemo, createContext, useContext, cloneElement } from 'react';
import { useIsSSR } from 'react-aria';
import { createPortal } from 'react-dom';
import { useSyncExternalStore as useSyncExternalStore$1 } from 'use-sync-external-store/shim/index.js';

var _excluded = ["value", "textValue", "id"];
var _Symbol$iterator, _Symbol$iterator2;

// This Collection implementation is perhaps a little unusual. It works by rendering the React tree into a
// Portal to a fake DOM implementation. This gives us efficient access to the tree of rendered objects, and
// supports React features like composition and context. We use this fake DOM to access the full set of elements
// before we render into the real DOM, which allows us to render a subset of the elements (e.g. virtualized scrolling),
// and compute properties like the total number of items. It also enables keyboard navigation, selection, and other features.
// React takes care of efficiently rendering components and updating the collection for us via this fake DOM.
//
// The DOM is a mutable API, and React expects the node instances to remain stable over time. So the implementation is split
// into two parts. Each mutable fake DOM node owns an instance of an immutable collection node. When a fake DOM node is updated,
// it queues a second render for the collection. Multiple updates to a collection can be queued at once. Collection nodes are
// lazily copied on write, so only the changed nodes need to be cloned. During the second render, the new immutable collection
// is finalized by updating the map of Key -> Node with the new cloned nodes. Then the new collection is frozen so it can no
// longer be mutated, and returned to the calling component to render.

/** An immutable object representing a Node in a Collection. */
var NodeValue = /*#__PURE__*/function () {
  function NodeValue(type, key) {
    _classCallCheck(this, NodeValue);
    _defineProperty(this, "value", null);
    _defineProperty(this, "level", 0);
    _defineProperty(this, "hasChildNodes", false);
    _defineProperty(this, "rendered", null);
    _defineProperty(this, "textValue", '');
    _defineProperty(this, 'aria-label', undefined);
    _defineProperty(this, "index", 0);
    _defineProperty(this, "parentKey", null);
    _defineProperty(this, "prevKey", null);
    _defineProperty(this, "nextKey", null);
    _defineProperty(this, "firstChildKey", null);
    _defineProperty(this, "lastChildKey", null);
    _defineProperty(this, "props", {});
    this.type = type;
    this.key = key;
  }
  _createClass(NodeValue, [{
    key: "childNodes",
    get: function get() {
      throw new Error('childNodes is not supported');
    }
  }, {
    key: "clone",
    value: function clone() {
      var node = new NodeValue(this.type, this.key);
      node.value = this.value;
      node.level = this.level;
      node.hasChildNodes = this.hasChildNodes;
      node.rendered = this.rendered;
      node.textValue = this.textValue;
      node['aria-label'] = this['aria-label'];
      node.index = this.index;
      node.parentKey = this.parentKey;
      node.prevKey = this.prevKey;
      node.nextKey = this.nextKey;
      node.firstChildKey = this.firstChildKey;
      node.lastChildKey = this.lastChildKey;
      node.props = this.props;
      return node;
    }
  }]);
  return NodeValue;
}();

/**
 * A mutable node in the fake DOM tree. When mutated, it marks itself as dirty
 * and queues an update with the owner document.
 */
_Symbol$iterator = Symbol.iterator;
var BaseNode = /*#__PURE__*/function () {
  function BaseNode(ownerDocument) {
    _classCallCheck(this, BaseNode);
    _defineProperty(this, "_firstChild", null);
    _defineProperty(this, "_lastChild", null);
    _defineProperty(this, "_previousSibling", null);
    _defineProperty(this, "_nextSibling", null);
    _defineProperty(this, "_parentNode", null);
    this.ownerDocument = ownerDocument;
  }
  _createClass(BaseNode, [{
    key: _Symbol$iterator,
    value: /*#__PURE__*/_regeneratorRuntime().mark(function value() {
      var node;
      return _regeneratorRuntime().wrap(function value$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            node = this.firstChild;
          case 1:
            if (!node) {
              _context.next = 7;
              break;
            }
            _context.next = 4;
            return node;
          case 4:
            node = node.nextSibling;
            _context.next = 1;
            break;
          case 7:
          case "end":
            return _context.stop();
        }
      }, value, this);
    })
  }, {
    key: "firstChild",
    get: function get() {
      return this._firstChild;
    }
  }, {
    key: "firstChild",
    set: function set(firstChild) {
      this._firstChild = firstChild;
      this.ownerDocument.markDirty(this);
    }
  }, {
    key: "lastChild",
    get: function get() {
      return this._lastChild;
    }
  }, {
    key: "lastChild",
    set: function set(lastChild) {
      this._lastChild = lastChild;
      this.ownerDocument.markDirty(this);
    }
  }, {
    key: "previousSibling",
    get: function get() {
      return this._previousSibling;
    }
  }, {
    key: "previousSibling",
    set: function set(previousSibling) {
      this._previousSibling = previousSibling;
      this.ownerDocument.markDirty(this);
    }
  }, {
    key: "nextSibling",
    get: function get() {
      return this._nextSibling;
    }
  }, {
    key: "nextSibling",
    set: function set(nextSibling) {
      this._nextSibling = nextSibling;
      this.ownerDocument.markDirty(this);
    }
  }, {
    key: "parentNode",
    get: function get() {
      return this._parentNode;
    }
  }, {
    key: "parentNode",
    set: function set(parentNode) {
      this._parentNode = parentNode;
      this.ownerDocument.markDirty(this);
    }
  }, {
    key: "isConnected",
    get: function get() {
      var _this$parentNode;
      return ((_this$parentNode = this.parentNode) === null || _this$parentNode === void 0 ? void 0 : _this$parentNode.isConnected) || false;
    }
  }, {
    key: "appendChild",
    value: function appendChild(child) {
      this.ownerDocument.startTransaction();
      if (child.parentNode) {
        child.parentNode.removeChild(child);
      }
      if (this.firstChild == null) {
        this.firstChild = child;
      }
      if (this.lastChild) {
        this.lastChild.nextSibling = child;
        child.index = this.lastChild.index + 1;
        child.previousSibling = this.lastChild;
      } else {
        child.previousSibling = null;
        child.index = 0;
      }
      child.parentNode = this;
      child.nextSibling = null;
      this.lastChild = child;
      this.ownerDocument.markDirty(this);
      if (child.hasSetProps) {
        // Only add the node to the collection if we already received props for it.
        // Otherwise wait until then so we have the correct id for the node.
        this.ownerDocument.addNode(child);
      }
      this.ownerDocument.endTransaction();
      this.ownerDocument.queueUpdate();
    }
  }, {
    key: "insertBefore",
    value: function insertBefore(newNode, referenceNode) {
      if (referenceNode == null) {
        return this.appendChild(newNode);
      }
      this.ownerDocument.startTransaction();
      if (newNode.parentNode) {
        newNode.parentNode.removeChild(newNode);
      }
      newNode.nextSibling = referenceNode;
      newNode.previousSibling = referenceNode.previousSibling;
      newNode.index = referenceNode.index;
      if (this.firstChild === referenceNode) {
        this.firstChild = newNode;
      } else if (referenceNode.previousSibling) {
        referenceNode.previousSibling.nextSibling = newNode;
      }
      referenceNode.previousSibling = newNode;
      newNode.parentNode = referenceNode.parentNode;
      var node = referenceNode;
      while (node) {
        node.index++;
        node = node.nextSibling;
      }
      if (newNode.hasSetProps) {
        this.ownerDocument.addNode(newNode);
      }
      this.ownerDocument.endTransaction();
      this.ownerDocument.queueUpdate();
    }
  }, {
    key: "removeChild",
    value: function removeChild(child) {
      if (child.parentNode !== this) {
        return;
      }
      this.ownerDocument.startTransaction();
      var node = child.nextSibling;
      while (node) {
        node.index--;
        node = node.nextSibling;
      }
      if (child.nextSibling) {
        child.nextSibling.previousSibling = child.previousSibling;
      }
      if (child.previousSibling) {
        child.previousSibling.nextSibling = child.nextSibling;
      }
      if (this.firstChild === child) {
        this.firstChild = child.nextSibling;
      }
      if (this.lastChild === child) {
        this.lastChild = child.previousSibling;
      }
      child.parentNode = null;
      child.nextSibling = null;
      child.previousSibling = null;
      child.index = 0;
      this.ownerDocument.removeNode(child);
      this.ownerDocument.endTransaction();
      this.ownerDocument.queueUpdate();
    }
  }, {
    key: "addEventListener",
    value: function addEventListener() {}
  }, {
    key: "removeEventListener",
    value: function removeEventListener() {}
  }]);
  return BaseNode;
}();
/**
 * A mutable element node in the fake DOM tree. It owns an immutable
 * Collection Node which is copied on write.
 */
var ElementNode = /*#__PURE__*/function (_BaseNode) {
  _inherits(ElementNode, _BaseNode);
  function ElementNode(type, ownerDocument) {
    var _this;
    _classCallCheck(this, ElementNode);
    _this = _callSuper(this, ElementNode, [ownerDocument]);
    _defineProperty(_assertThisInitialized(_this), "nodeType", 8);
    _defineProperty(_assertThisInitialized(_this), "_index", 0);
    _defineProperty(_assertThisInitialized(_this), "hasSetProps", false);
    _this.node = new NodeValue(type, "react-aria-".concat(++ownerDocument.nodeId));
    // Start a transaction so that no updates are emitted from the collection
    // until the props for this node are set. We don't know the real id for the
    // node until then, so we need to avoid emitting collections in an inconsistent state.
    _this.ownerDocument.startTransaction();
    return _this;
  }
  _createClass(ElementNode, [{
    key: "index",
    get: function get() {
      return this._index;
    },
    set: function set(index) {
      this._index = index;
      this.ownerDocument.markDirty(this);
    }
  }, {
    key: "level",
    get: function get() {
      if (this.parentNode instanceof ElementNode) {
        return this.parentNode.level + (this.node.type === 'item' ? 1 : 0);
      }
      return 0;
    }
  }, {
    key: "updateNode",
    value: function updateNode() {
      var _this$previousSibling, _this$previousSibling2, _this$nextSibling$nod, _this$nextSibling, _this$firstChild$node, _this$firstChild, _this$lastChild$node$, _this$lastChild;
      var node = this.ownerDocument.getMutableNode(this);
      node.index = this.index;
      node.level = this.level;
      node.parentKey = this.parentNode instanceof ElementNode ? this.parentNode.node.key : null;
      node.prevKey = (_this$previousSibling = (_this$previousSibling2 = this.previousSibling) === null || _this$previousSibling2 === void 0 ? void 0 : _this$previousSibling2.node.key) !== null && _this$previousSibling !== void 0 ? _this$previousSibling : null;
      node.nextKey = (_this$nextSibling$nod = (_this$nextSibling = this.nextSibling) === null || _this$nextSibling === void 0 ? void 0 : _this$nextSibling.node.key) !== null && _this$nextSibling$nod !== void 0 ? _this$nextSibling$nod : null;
      node.hasChildNodes = !!this.firstChild;
      node.firstChildKey = (_this$firstChild$node = (_this$firstChild = this.firstChild) === null || _this$firstChild === void 0 ? void 0 : _this$firstChild.node.key) !== null && _this$firstChild$node !== void 0 ? _this$firstChild$node : null;
      node.lastChildKey = (_this$lastChild$node$ = (_this$lastChild = this.lastChild) === null || _this$lastChild === void 0 ? void 0 : _this$lastChild.node.key) !== null && _this$lastChild$node$ !== void 0 ? _this$lastChild$node$ : null;
    }
  }, {
    key: "setProps",
    value: function setProps(obj, ref, rendered) {
      var node = this.ownerDocument.getMutableNode(this);
      var value = obj.value,
        textValue = obj.textValue,
        id = obj.id,
        props = _objectWithoutProperties(obj, _excluded);
      props.ref = ref;
      node.props = props;
      node.rendered = rendered;
      node.value = value;
      node.textValue = textValue || (typeof rendered === 'string' ? rendered : '') || obj['aria-label'] || '';
      if (id != null && id !== node.key) {
        if (this.hasSetProps) {
          throw new Error('Cannot change the id of an item');
        }
        node.key = id;
      }

      // If this is the first time props have been set, end the transaction started in the constructor
      // so this node can be emitted.
      if (!this.hasSetProps) {
        this.ownerDocument.addNode(this);
        this.ownerDocument.endTransaction();
        this.hasSetProps = true;
      }
      this.ownerDocument.queueUpdate();
    }
  }, {
    key: "style",
    get: function get() {
      return {};
    }
  }, {
    key: "hasAttribute",
    value: function hasAttribute() {}
  }, {
    key: "setAttribute",
    value: function setAttribute() {}
  }, {
    key: "setAttributeNS",
    value: function setAttributeNS() {}
  }, {
    key: "removeAttribute",
    value: function removeAttribute() {}
  }]);
  return ElementNode;
}(BaseNode);

/**
 * An immutable Collection implementation. Updates are only allowed
 * when it is not marked as frozen.
 */
_Symbol$iterator2 = Symbol.iterator;
var BaseCollection = /*#__PURE__*/function () {
  function BaseCollection() {
    _classCallCheck(this, BaseCollection);
    _defineProperty(this, "keyMap", new Map());
    _defineProperty(this, "firstKey", null);
    _defineProperty(this, "lastKey", null);
    _defineProperty(this, "frozen", false);
  }
  _createClass(BaseCollection, [{
    key: "size",
    get: function get() {
      return this.keyMap.size;
    }
  }, {
    key: "getKeys",
    value: function getKeys() {
      return this.keyMap.keys();
    }
  }, {
    key: _Symbol$iterator2,
    value: /*#__PURE__*/_regeneratorRuntime().mark(function value() {
      var node;
      return _regeneratorRuntime().wrap(function value$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            node = this.firstKey != null ? this.keyMap.get(this.firstKey) : undefined;
          case 1:
            if (!node) {
              _context2.next = 7;
              break;
            }
            _context2.next = 4;
            return node;
          case 4:
            node = node.nextKey != null ? this.keyMap.get(node.nextKey) : undefined;
            _context2.next = 1;
            break;
          case 7:
          case "end":
            return _context2.stop();
        }
      }, value, this);
    })
  }, {
    key: "getChildren",
    value: function getChildren(key) {
      var keyMap = this.keyMap;
      return _defineProperty({}, Symbol.iterator, /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        var parent, node;
        return _regeneratorRuntime().wrap(function _callee$(_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              parent = keyMap.get(key);
              node = (parent === null || parent === void 0 ? void 0 : parent.firstChildKey) != null ? keyMap.get(parent.firstChildKey) : null;
            case 2:
              if (!node) {
                _context3.next = 8;
                break;
              }
              _context3.next = 5;
              return node;
            case 5:
              node = node.nextKey != null ? keyMap.get(node.nextKey) : undefined;
              _context3.next = 2;
              break;
            case 8:
            case "end":
              return _context3.stop();
          }
        }, _callee);
      }));
    }
  }, {
    key: "getKeyBefore",
    value: function getKeyBefore(key) {
      var node = this.keyMap.get(key);
      if (!node) {
        return null;
      }
      if (node.prevKey != null) {
        var _node$key, _node;
        node = this.keyMap.get(node.prevKey);
        while (node && node.type !== 'item' && node.lastChildKey != null) {
          node = this.keyMap.get(node.lastChildKey);
        }
        return (_node$key = (_node = node) === null || _node === void 0 ? void 0 : _node.key) !== null && _node$key !== void 0 ? _node$key : null;
      }
      return node.parentKey;
    }
  }, {
    key: "getKeyAfter",
    value: function getKeyAfter(key) {
      var node = this.keyMap.get(key);
      if (!node) {
        return null;
      }
      if (node.type !== 'item' && node.firstChildKey != null) {
        return node.firstChildKey;
      }
      while (node) {
        if (node.nextKey != null) {
          return node.nextKey;
        }
        if (node.parentKey != null) {
          node = this.keyMap.get(node.parentKey);
        } else {
          return null;
        }
      }
      return null;
    }
  }, {
    key: "getFirstKey",
    value: function getFirstKey() {
      return this.firstKey;
    }
  }, {
    key: "getLastKey",
    value: function getLastKey() {
      var _node$key2, _node3;
      var node = this.lastKey != null ? this.keyMap.get(this.lastKey) : null;
      while (((_node2 = node) === null || _node2 === void 0 ? void 0 : _node2.lastChildKey) != null) {
        var _node2;
        node = this.keyMap.get(node.lastChildKey);
      }
      return (_node$key2 = (_node3 = node) === null || _node3 === void 0 ? void 0 : _node3.key) !== null && _node$key2 !== void 0 ? _node$key2 : null;
    }
  }, {
    key: "getItem",
    value: function getItem(key) {
      var _this$keyMap$get;
      return (_this$keyMap$get = this.keyMap.get(key)) !== null && _this$keyMap$get !== void 0 ? _this$keyMap$get : null;
    }
  }, {
    key: "at",
    value: function at() {
      throw new Error('Not implemented');
    }
  }, {
    key: "clone",
    value: function clone() {
      // We need to clone using this.constructor so that subclasses have the right prototype.
      // TypeScript isn't happy about this yet.
      // https://github.com/microsoft/TypeScript/issues/3841
      var Constructor = this.constructor;
      var collection = new Constructor();
      collection.keyMap = new Map(this.keyMap);
      collection.firstKey = this.firstKey;
      collection.lastKey = this.lastKey;
      return collection;
    }
  }, {
    key: "addNode",
    value: function addNode(node) {
      if (this.frozen) {
        throw new Error('Cannot add a node to a frozen collection');
      }
      this.keyMap.set(node.key, node);
    }
  }, {
    key: "removeNode",
    value: function removeNode(key) {
      if (this.frozen) {
        throw new Error('Cannot remove a node to a frozen collection');
      }
      this.keyMap["delete"](key);
    }
  }, {
    key: "commit",
    value: function commit(firstKey, lastKey) {
      var isSSR = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
      if (this.frozen) {
        throw new Error('Cannot commit a frozen collection');
      }
      this.firstKey = firstKey;
      this.lastKey = lastKey;
      this.frozen = !isSSR;
    }
  }]);
  return BaseCollection;
}();

/**
 * A mutable Document in the fake DOM. It owns an immutable Collection instance,
 * which is lazily copied on write during updates.
 */
var Document = /*#__PURE__*/function (_BaseNode2) {
  _inherits(Document, _BaseNode2);
  function Document(collection) {
    var _this2;
    _classCallCheck(this, Document);
    // @ts-ignore
    _this2 = _callSuper(this, Document, [null]);
    _defineProperty(_assertThisInitialized(_this2), "nodeType", 11);
    // DOCUMENT_FRAGMENT_NODE
    _defineProperty(_assertThisInitialized(_this2), "ownerDocument", _assertThisInitialized(_this2));
    _defineProperty(_assertThisInitialized(_this2), "dirtyNodes", new Set());
    _defineProperty(_assertThisInitialized(_this2), "isSSR", false);
    _defineProperty(_assertThisInitialized(_this2), "nodeId", 0);
    _defineProperty(_assertThisInitialized(_this2), "nodesByProps", new WeakMap());
    _defineProperty(_assertThisInitialized(_this2), "mutatedNodes", new Set());
    _defineProperty(_assertThisInitialized(_this2), "subscriptions", new Set());
    _defineProperty(_assertThisInitialized(_this2), "transactionCount", 0);
    _this2.collection = collection;
    _this2.collectionMutated = true;
    return _this2;
  }
  _createClass(Document, [{
    key: "isConnected",
    get: function get() {
      return true;
    }
  }, {
    key: "createElement",
    value: function createElement(type) {
      return new ElementNode(type, this);
    }

    /**
     * Lazily gets a mutable instance of a Node. If the node has already
     * been cloned during this update cycle, it just returns the existing one.
     */
  }, {
    key: "getMutableNode",
    value: function getMutableNode(element) {
      var node = element.node;
      if (!this.mutatedNodes.has(element)) {
        node = element.node.clone();
        this.mutatedNodes.add(element);
        element.node = node;
      }
      this.markDirty(element);
      return node;
    }
  }, {
    key: "getMutableCollection",
    value: function getMutableCollection() {
      if (!this.isSSR && !this.collectionMutated) {
        this.collection = this.collection.clone();
        this.collectionMutated = true;
      }
      return this.collection;
    }
  }, {
    key: "markDirty",
    value: function markDirty(node) {
      this.dirtyNodes.add(node);
    }
  }, {
    key: "startTransaction",
    value: function startTransaction() {
      this.transactionCount++;
    }
  }, {
    key: "endTransaction",
    value: function endTransaction() {
      this.transactionCount--;
    }
  }, {
    key: "addNode",
    value: function addNode(element) {
      var collection = this.getMutableCollection();
      if (!collection.getItem(element.node.key)) {
        collection.addNode(element.node);
        var _iterator = _createForOfIteratorHelper(element),
          _step;
        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var child = _step.value;
            this.addNode(child);
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
      }
      this.markDirty(element);
    }
  }, {
    key: "removeNode",
    value: function removeNode(node) {
      var _iterator2 = _createForOfIteratorHelper(node),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var child = _step2.value;
          this.removeNode(child);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
      var collection = this.getMutableCollection();
      collection.removeNode(node.node.key);
      this.markDirty(node);
    }

    /** Finalizes the collection update, updating all nodes and freezing the collection. */
  }, {
    key: "getCollection",
    value: function getCollection() {
      if (this.transactionCount > 0) {
        return this.collection;
      }
      this.updateCollection();
      return this.collection;
    }
  }, {
    key: "updateCollection",
    value: function updateCollection() {
      var _iterator3 = _createForOfIteratorHelper(this.dirtyNodes),
        _step3;
      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var _element = _step3.value;
          if (_element instanceof ElementNode && _element.isConnected) {
            _element.updateNode();
          }
        }
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
      this.dirtyNodes.clear();
      if (this.mutatedNodes.size || this.collectionMutated) {
        var _this$firstChild$node2, _this$firstChild2, _this$lastChild$node$2, _this$lastChild2;
        var collection = this.getMutableCollection();
        var _iterator4 = _createForOfIteratorHelper(this.mutatedNodes),
          _step4;
        try {
          for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
            var element = _step4.value;
            if (element.isConnected) {
              collection.addNode(element.node);
            }
          }
        } catch (err) {
          _iterator4.e(err);
        } finally {
          _iterator4.f();
        }
        collection.commit((_this$firstChild$node2 = (_this$firstChild2 = this.firstChild) === null || _this$firstChild2 === void 0 ? void 0 : _this$firstChild2.node.key) !== null && _this$firstChild$node2 !== void 0 ? _this$firstChild$node2 : null, (_this$lastChild$node$2 = (_this$lastChild2 = this.lastChild) === null || _this$lastChild2 === void 0 ? void 0 : _this$lastChild2.node.key) !== null && _this$lastChild$node$2 !== void 0 ? _this$lastChild$node$2 : null, this.isSSR);
        this.mutatedNodes.clear();
      }
      this.collectionMutated = false;
    }
  }, {
    key: "queueUpdate",
    value: function queueUpdate() {
      // Don't emit any updates if there is a transaction in progress.
      // queueUpdate should be called again after the transaction.
      if (this.dirtyNodes.size === 0 || this.transactionCount > 0) {
        return;
      }
      var _iterator5 = _createForOfIteratorHelper(this.subscriptions),
        _step5;
      try {
        for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
          var fn = _step5.value;
          fn();
        }
      } catch (err) {
        _iterator5.e(err);
      } finally {
        _iterator5.f();
      }
    }
  }, {
    key: "subscribe",
    value: function subscribe(fn) {
      var _this3 = this;
      this.subscriptions.add(fn);
      return function () {
        return _this3.subscriptions["delete"](fn);
      };
    }
  }, {
    key: "resetAfterSSR",
    value: function resetAfterSSR() {
      if (this.isSSR) {
        this.isSSR = false;
        this.firstChild = null;
        this.lastChild = null;
        this.nodeId = 0;
      }
    }
  }]);
  return Document;
}(BaseNode);
function useCachedChildren(props) {
  var children = props.children,
    items = props.items,
    idScope = props.idScope,
    addIdAndValue = props.addIdAndValue,
    _props$dependencies = props.dependencies,
    dependencies = _props$dependencies === void 0 ? [] : _props$dependencies;

  // Invalidate the cache whenever the parent value changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var cache = useMemo(function () {
    return new WeakMap();
  }, dependencies);
  return useMemo(function () {
    if (items && typeof children === 'function') {
      var res = [];
      var _iterator6 = _createForOfIteratorHelper(items),
        _step6;
      try {
        for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
          var _item = _step6.value;
          var rendered = cache.get(_item);
          if (!rendered) {
            var _ref2, _rendered$props$id;
            rendered = children(_item);
            // @ts-ignore
            var key = (_ref2 = (_rendered$props$id = rendered.props.id) !== null && _rendered$props$id !== void 0 ? _rendered$props$id : _item.key) !== null && _ref2 !== void 0 ? _ref2 : _item.id;
            // eslint-disable-next-line max-depth
            if (key == null) {
              throw new Error('Could not determine key for item');
            }
            // eslint-disable-next-line max-depth
            if (idScope) {
              key = idScope + ':' + key;
            }
            // Note: only works if wrapped Item passes through id...
            rendered = /*#__PURE__*/cloneElement(rendered, addIdAndValue ? {
              key: key,
              id: key,
              value: _item
            } : {
              key: key
            });
            cache.set(_item, rendered);
          }
          res.push(rendered);
        }
      } catch (err) {
        _iterator6.e(err);
      } finally {
        _iterator6.f();
      }
      return res;
    } else if (typeof children !== 'function') {
      return children;
    }
    return null;
  }, [children, items, cache, idScope, addIdAndValue]);
}
function useCollectionChildren(props) {
  return useCachedChildren(_objectSpread2(_objectSpread2({}, props), {}, {
    addIdAndValue: true
  }));
}
var ShallowRenderContext = /*#__PURE__*/createContext(false);
function useCollection(props, initialCollection) {
  var _useCollectionDocumen = useCollectionDocument(initialCollection),
    collection = _useCollectionDocumen.collection,
    document = _useCollectionDocumen.document;
  var portal = useCollectionPortal(props, document);
  return {
    portal: portal,
    collection: collection
  };
}
// React 16 and 17 don't support useSyncExternalStore natively, and the shim provided by React does not support getServerSnapshot.
// This wrapper uses the shim, but additionally calls getServerSnapshot during SSR (according to SSRProvider).
function useSyncExternalStoreFallback(subscribe, getSnapshot, getServerSnapshot) {
  var isSSR = useIsSSR();
  var isSSRRef = useRef(isSSR);
  // This is read immediately inside the wrapper, which also runs during render.
  // We just need a ref to avoid invalidating the callback itself, which
  // would cause React to re-run the callback more than necessary.
  isSSRRef.current = isSSR;
  var getSnapshotWrapper = useCallback(function () {
    return isSSRRef.current ? getServerSnapshot() : getSnapshot();
  }, [getSnapshot, getServerSnapshot]);
  return useSyncExternalStore$1(subscribe, getSnapshotWrapper);
}
var useSyncExternalStore = typeof React['useSyncExternalStore'] === 'function' ? React['useSyncExternalStore'] : useSyncExternalStoreFallback;
function useCollectionDocument(initialCollection) {
  // The document instance is mutable, and should never change between renders.
  // useSyncExternalStore is used to subscribe to updates, which vends immutable Collection objects.
  var document = useMemo(function () {
    return new Document(initialCollection || new BaseCollection());
  }, [initialCollection]);
  var subscribe = useCallback(function (fn) {
    return document.subscribe(fn);
  }, [document]);
  var getSnapshot = useCallback(function () {
    var collection = document.getCollection();
    if (document.isSSR) {
      // After SSR is complete, reset the document to empty so it is ready for React to render the portal into.
      // We do this _after_ getting the collection above so that the collection still has content in it from SSR
      // during the current render, before React has finished the client render.
      document.resetAfterSSR();
    }
    return collection;
  }, [document]);
  var getServerSnapshot = useCallback(function () {
    document.isSSR = true;
    return document.getCollection();
  }, [document]);
  var collection = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    collection: collection,
    document: document
  };
}
var SSRContext = /*#__PURE__*/createContext(null);
var CollectionDocumentContext = /*#__PURE__*/createContext(null);
function useCollectionPortal(props, document) {
  var ctx = useContext(CollectionDocumentContext);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  var doc = document !== null && document !== void 0 ? document : ctx;
  var children = useCollectionChildren(props);
  var wrappedChildren = useMemo(function () {
    return /*#__PURE__*/React.createElement(ShallowRenderContext.Provider, {
      value: true
    }, children);
  }, [children]);
  // During SSR, we render the content directly, and append nodes to the document during render.
  // The collection children return null so that nothing is actually rendered into the HTML.
  return useIsSSR() ? /*#__PURE__*/React.createElement(SSRContext.Provider, {
    value: doc
  }, wrappedChildren) : ( /*#__PURE__*/createPortal(wrappedChildren, doc));
}
function CollectionPortal(props) {
  return /*#__PURE__*/React.createElement(React.Fragment, null, useCollectionPortal(props));
}
function useCollectionItemRef(props, ref, rendered) {
  // Return a callback ref that sets the props object on the fake DOM node.
  return useCallback(function (element) {
    element === null || element === void 0 || element.setProps(props, ref, rendered);
  }, [props, ref, rendered]);
}
function useSSRCollectionNode(Type, props, ref, rendered, children) {
  // During SSR, portals are not supported, so the collection children will be wrapped in an SSRContext.
  // Since SSR occurs only once, we assume that the elements are rendered in order and never re-render.
  // Therefore we can create elements in our collection document during render so that they are in the
  // collection by the time we need to use the collection to render to the real DOM.
  // After hydration, we switch to client rendering using the portal.
  var itemRef = useCollectionItemRef(props, ref, rendered);
  var parentNode = useContext(SSRContext);
  if (parentNode) {
    // Guard against double rendering in strict mode.
    var element = parentNode.ownerDocument.nodesByProps.get(props);
    if (!element) {
      element = parentNode.ownerDocument.createElement(Type);
      element.setProps(props, ref, rendered);
      parentNode.appendChild(element);
      parentNode.ownerDocument.updateCollection();
      parentNode.ownerDocument.nodesByProps.set(props, element);
    }
    return children ? /*#__PURE__*/React.createElement(SSRContext.Provider, {
      value: element
    }, children) : null;
  }

  // @ts-ignore
  return /*#__PURE__*/React.createElement(Type, {
    ref: itemRef
  }, children);
}
function selectedKeyToNode(selectedKey, collection) {
  return selectedKey != null ? Array.isArray(selectedKey) ? selectedKey.reduce(function (acc, cur) {
    var node = collection.getItem(cur);
    return node ? [].concat(_toConsumableArray(acc), [node]) : acc;
  }, []) : collection.getItem(selectedKey) : null;
}

export { BaseCollection, CollectionDocumentContext, CollectionPortal, Document, ElementNode, NodeValue, selectedKeyToNode, useCachedChildren, useCollection, useCollectionChildren, useCollectionDocument, useCollectionItemRef, useCollectionPortal, useSSRCollectionNode };
//# sourceMappingURL=collection.esm.js.map
